import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";
import { Eye, EyeOff, Mail } from "lucide-react";

/** /socio/login — fully localized ES / FR / EN. "Socio referidor" is kept
 *  untranslated as a proper noun across all three languages. */
const T = {
  es: {
    title: "Ingreso Socio referidor",
    lead: "Espacio Socio referidor",
    emailLbl: "Email",
    passwordLbl: "Contraseña",
    submit: "Ingresar",
    submitting: "Ingresando…",
    welcome: "Bienvenido",
    forgot: "¿Olvidó su contraseña?",
    noAccount: "¿No tienes cuenta?",
    becomePartner: "Conviértase en Socio referidor",
    resetTitle: "Restablecer contraseña",
    resetLead: <>Solo tu cuenta <strong>Socio referidor</strong> — no afecta tu cuenta médico ni paciente.</>,
    resetEmailLbl: "Email de tu cuenta Socio referidor",
    resetEmailMissing: "Ingresa tu email",
    resetSubmit: "Enviar enlace",
    resetSent: "Si tu email existe, te enviamos un enlace para restablecer tu contraseña. El enlace es válido por 1 hora.",
    resetBack: "Volver",
    resetCancel: "Cancelar",
    errorGeneric: "Error",
  },
  fr: {
    title: "Connexion Socio referidor",
    lead: "Espace Socio referidor",
    emailLbl: "Email",
    passwordLbl: "Mot de passe",
    submit: "Se connecter",
    submitting: "Connexion…",
    welcome: "Bienvenue",
    forgot: "Mot de passe oublié ?",
    noAccount: "Pas encore de compte ?",
    becomePartner: "Devenir Socio referidor",
    resetTitle: "Réinitialiser le mot de passe",
    resetLead: <>Uniquement votre compte <strong>Socio referidor</strong> — ni votre compte médecin ni votre compte patient ne sont affectés.</>,
    resetEmailLbl: "Email de votre compte Socio referidor",
    resetEmailMissing: "Saisissez votre email",
    resetSubmit: "Envoyer le lien",
    resetSent: "Si votre email existe, nous vous avons envoyé un lien pour réinitialiser votre mot de passe. Le lien est valable 1 heure.",
    resetBack: "Retour",
    resetCancel: "Annuler",
    errorGeneric: "Erreur",
  },
  en: {
    title: "Socio referidor sign-in",
    lead: "Socio referidor space",
    emailLbl: "Email",
    passwordLbl: "Password",
    submit: "Sign in",
    submitting: "Signing in…",
    welcome: "Welcome",
    forgot: "Forgot your password?",
    noAccount: "No account yet?",
    becomePartner: "Become a Socio referidor",
    resetTitle: "Reset password",
    resetLead: <>Only your <strong>Socio referidor</strong> account — your doctor and patient accounts are not affected.</>,
    resetEmailLbl: "Email of your Socio referidor account",
    resetEmailMissing: "Enter your email",
    resetSubmit: "Send link",
    resetSent: "If your email exists, we've sent you a link to reset your password. The link is valid for 1 hour.",
    resetBack: "Back",
    resetCancel: "Cancel",
    errorGeneric: "Error",
  },
};

export default function PartnerLogin() {
  const { login, token, role, lang } = useApp();
  const t = T[lang] || T.es;
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const next = sp.get("next") || "/socio/panel";
  const [mode, setMode] = useState("login"); // login | forgot
  const [form, setForm] = useState({ email: "", password: "" });
  const [resetEmail, setResetEmail] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (token && role === "partner") nav(next, { replace: true });
  }, [token, role, nav, next]);

  const submit = async () => {
    setLoading(true);
    try {
      const r = await api.post("/partner/login", form);
      login(r.data.token, "partner", { email: r.data.email, code: r.data.code });
      toast.success(t.welcome); nav(next);
    } catch (e) { toast.error(e.response?.data?.detail || t.errorGeneric); }
    finally { setLoading(false); }
  };

  const requestReset = async () => {
    if (!resetEmail) return toast.error(t.resetEmailMissing);
    setLoading(true);
    try {
      await api.post("/partner/password/request-reset", { email: resetEmail });
      setSent(true);
    } catch (e) { toast.error(e.response?.data?.detail || t.errorGeneric); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-12">
      <Card><CardContent className="p-6 sm:p-8">
        {mode === "login" ? (
          <>
            <h1 className="text-2xl font-bold text-slate-900 mb-1">{t.title}</h1>
            <p className="text-sm text-slate-500 mb-5">{t.lead}</p>
            <div className="space-y-3">
              <div><Label>{t.emailLbl}</Label>
                <Input data-testid="partner-login-email" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})}/></div>
              <div><Label>{t.passwordLbl}</Label>
                <div className="relative">
                  <Input data-testid="partner-login-pw" type={showPw ? "text" : "password"}
                         value={form.password} onChange={e => setForm({...form, password: e.target.value})}/>
                  <button type="button" data-testid="toggle-pw" onClick={() => setShowPw(!showPw)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
                    {showPw ? <EyeOff size={16}/> : <Eye size={16}/>}
                  </button>
                </div>
              </div>
            </div>
            <Button data-testid="partner-login-submit" onClick={submit} disabled={loading}
                    className="w-full mt-5 h-11 bg-blue-600 hover:bg-blue-700 font-semibold">
              {loading ? t.submitting : t.submit}
            </Button>
            <button data-testid="partner-forgot-btn" onClick={() => setMode("forgot")}
                    className="w-full text-center text-sm text-blue-600 hover:text-blue-800 mt-4">
              {t.forgot}
            </button>
            <div className="text-center text-xs text-slate-500 mt-3">
              {t.noAccount} <Link to="/socio/registro" className="text-blue-600 hover:underline">{t.becomePartner}</Link>
            </div>
          </>
        ) : (
          <>
            <h1 className="text-2xl font-bold text-slate-900 mb-1">{t.resetTitle}</h1>
            <p className="text-sm text-slate-500 mb-5">{t.resetLead}</p>
            {sent ? (
              <div className="text-center py-4">
                <Mail size={40} className="text-emerald-600 mx-auto mb-2"/>
                <p data-testid="partner-reset-sent" className="text-sm text-slate-700">
                  {t.resetSent}
                </p>
                <Button data-testid="partner-reset-back-btn" onClick={() => { setMode("login"); setSent(false); }}
                        className="mt-5 bg-blue-600 hover:bg-blue-700">{t.resetBack}</Button>
              </div>
            ) : (
              <>
                <Label>{t.resetEmailLbl}</Label>
                <Input data-testid="partner-reset-email" type="email" value={resetEmail}
                       onChange={e => setResetEmail(e.target.value)}/>
                <Button data-testid="partner-reset-submit" onClick={requestReset} disabled={loading}
                        className="w-full mt-4 bg-blue-600 hover:bg-blue-700">
                  {t.resetSubmit}
                </Button>
                <button data-testid="partner-reset-cancel" onClick={() => setMode("login")}
                        className="w-full text-sm text-slate-500 hover:text-blue-600 mt-3">
                  {t.resetCancel}
                </button>
              </>
            )}
          </>
        )}
      </CardContent></Card>
    </div>
  );
}
